import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/Users/wcandillon/tmp/tmp/react-native-redash/packages/docs/node_modules/gatsby-theme-docz/src/base/Layout.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "hooks"
    }}>{`Hooks`}</h1>
    <hr></hr>
    <p>{`The hooks provided by redash are here so you can create animation values, clocks, and event nodes that will keep their identity across component re-renders.`}</p>
    <h2 {...{
      "id": "usevalue"
    }}><inlineCode parentName="h2">{`useValue()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const useValue: <V extends string | number | boolean>(value: V) => Animated.Value<V>;
`}</code></pre>
    <p>{`Creates an animation value which identity is preserved during the whole component lifecycle.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const toggle = useValue(0);
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "usevalues"
    }}><inlineCode parentName="h2">{`useValues()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const useValues: <V extends string | number | boolean>(values: V[]) => Animated.Value<V>[];
`}</code></pre>
    <p>{`Creates an animation values which identity is preserved during the whole component lifecycle.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const [toggle, state] = useValues(0, State.UNDETERMINED);
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "useclock"
    }}><inlineCode parentName="h2">{`useClock()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const useClock: () => Animated.Clock;
`}</code></pre>
    <p>{`Creates a clock which identity is preserved during the whole component lifecycle.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const clock = useClock();
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "useclocks"
    }}><inlineCode parentName="h2">{`useClocks()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const useClocks: (numberOfClocks: number) => Animated.Clock[];
`}</code></pre>
    <p>{`Creates clocks which identity is preserved during the whole component lifecycle.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const [clock1, clock2, clock3] = useClocks(3);
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "usevector"
    }}><inlineCode parentName="h2">{`useVector()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const useVector: (x: number, y: number) => Vector<Animated.Value>;
`}</code></pre>
    <p>{`Creates a vector which identity is preserved by `}<inlineCode parentName="p">{`deps`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const vector = useVector(width, height);
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "usegesturehandler"
    }}><inlineCode parentName="h2">{`useGestureHandler()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const useGestureHandler = (
  nativeEvent: Parameters<typeof onGestureEvent>[0])
`}</code></pre>
    <p>{`Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const state = useValue(State.UNDETERMINED);
const gestureHandler = useGestureHandler({ state });
return (
  <TapGestureHandler {...gestureHandler}>
  //...
  </TapGestureHandler>
)
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "usepangesturehandler"
    }}><inlineCode parentName="h2">{`usePanGestureHandler()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const usePanGestureHandler = () => {
  position: Vector<Animated.Value<number>>,
  translation: Vector<Animated.Value<number>>,
  velocity: Vector<Animated.Value<number>>,
  state: Animated.Value<State>,
  gestureHandler: Animated.Node<number>
};
`}</code></pre>
    <p>{`Example:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const {gestureHandler, state, translation} = usePanGestureHandler();
return (
  <PanGestureHandler {...gestureHandler}>
    <Animated.View style={{ flex: 1, backgroundColor: "cyan", transform: [{ translateX: translation.x }] }} />
  </PanGestureHandler>
)
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "usepinchgesturehandler"
    }}><inlineCode parentName="h2">{`usePinchGestureHandler()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const {gestureHandler, state, scale, focal} = usePanGestureHandler();
return (
  <PinchGestureHandler {...gestureHandler}>
    <Image style={{ transform: [{ scale }] }} {...{source}} />
  </PinchGestureHandler>
);
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "usetapgesturehandler"
    }}><inlineCode parentName="h2">{`useTapGestureHandler()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const {gestureHandler, position, absolutePosition} = useTapGestureHandler();
return (
  <TapGestureHandler {...gestureHandler}>
    <Text>Tap me!</Text>
  </TapGestureHandler>
);
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "usediff"
    }}><inlineCode parentName="h2">{`useDiff()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const useDiff: (node: Animated.Node<number>) => Animated.Value<number>;
`}</code></pre>
    <p>{`Returns a difference between the node value from the last frame and the current one.
`}<inlineCode parentName="p">{`useDiff`}</inlineCode>{` works for every frame whereas `}<inlineCode parentName="p">{`diff`}</inlineCode>{` works on every evaluation.`}</p>
    <p>{`Examples:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const [state, x, y] = useValues(State.UNDETERMINED, 0, 0);
const deltaX = useDiff(x);
const deltaY = useDiff(y);
`}</code></pre>
    <hr></hr>
    <h2 {...{
      "id": "usedebug"
    }}><inlineCode parentName="h2">{`useDebug()`}</inlineCode></h2>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`const useDebug: (values: { [key: string]: Animated.Node<number> });
`}</code></pre>
    <p>{`Examples:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`useDebug({
  translateX,
  translateY
})
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      